import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { Form, Field, Formik, FormikProps } from 'formik';
import { RootState } from 'store/store';
import { IOption } from 'components/Select/Select';
import Button from 'components/Button';
import Select from 'components/Select';
import { display } from 'components/ErrorView/ErrorDialog';
import SurveySettingsForm from './SurveySettingsForm';
import notify from 'components/Notify';
import { getTags } from './GeneralQuestion/GeneralQuestionEditor';
import { getAccessToken } from 'api/Utils';
import { SurveyAnalyseTemplateType } from 'store/survey/surveyTypes';
import {
  requireProviderTags,
  requireProviderTagsFn,
  updateSurvey as providerUpdateSurvey,
  updateSurveyFn as providerUpdateSurveyFn,
} from 'store/providers/providerSlice';
import { appDispatch } from 'store/store';
import { ICompany } from 'store/companies/companiesTypes';
import {
  ISurveyItem,
  ISurveyItemWithData,
  ISurveyView,
  ISurveyViewWithData,
} from 'store/survey/surveyTypes';
import {
  addToPhoneValidList,
  addToPhoneValidListFn,
  removePhoneValidList,
  removePhoneValidListFn,
  requireCheckPoints,
  requireCheckPointsFn,
  updateSurvey,
  updateSurveyFn,
  applyAnalyseTemplate,
  applyAnalyseTemplateFn,
} from 'store/survey/surveySlice';
import {
  getCompanyFn,
  getCompanyHandler,
} from 'store/companies/companiesSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import { ITags, ITagView } from 'store/providers/providerTypes';
import APopover from '../../../../APopover';
import { isANumber } from 'utils/number';
import { green, red } from 'utils/style/colors';
import { HintIcon, HintText, QuestionHintBox } from './GeneralQuestion/styles';
import { notifyError } from 'components/Notify/Notify';
import Icon from '../../../../Icon';
import {
  isCompanySupport,
  isAdvanceAdmin,
} from '../../../../../store/companies/companiesUtils';
import i18n from '../../../../../i18n';

interface IStateToProps {
  roles?: ICompany['roles'];
  companyMembers?: ICompany['members'];
  companyId?: number;
  tags?: ITags['data'];
  token: string;
  supportOrOwner: boolean;
  isAdvancedAdmin: boolean;
}

// TODO: define types with "typeof" for dispatches
interface IDispatchToProps {
  updateSurvey: (
    surveyData: ISurveyView,
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof updateSurveyFn>;
  requireProviderTags: (
    providerId: number
  ) => ReturnType<typeof requireProviderTagsFn>;
  getCompany: (companyId: number) => ReturnType<typeof getCompanyFn>;
  updateProviderSurvey: (
    surveyItem: ISurveyItemWithData
  ) => ReturnType<typeof providerUpdateSurveyFn>;
  requireCheckPoints: (
    surveyId?: number,
    reportId?: number,
    surveyToken?: string,
    ownerToken?: string
  ) => ReturnType<typeof requireCheckPointsFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  addToPhoneValidList: (
    validList: string[],
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof addToPhoneValidListFn>;
  applyAnalyseTemplate: (
    providerId: number
  ) => ReturnType<typeof applyAnalyseTemplateFn>;
  removeValidList: (
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof removePhoneValidListFn>;
}
interface IOwnProps {
  item: ISurveyItemWithData;
  survey: ISurveyViewWithData;
  allTags?: { [tagId: string]: ITagView };
  withoutProviderSurvey?: boolean;
  onlyEditNotifGroups?: boolean;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  dataError?: any;
  savingData: boolean;
  savingSurveyItem: boolean;
  convertingToExam?: boolean;
  showCheckPointModal: boolean;
  validPhoneValues: string[];
  updatingValidList: boolean;
  applyingTemplate: boolean;
}

const getSelectedTags = (selectedTags: ITagView[]) => {
  return selectedTags.map((tag: ITagView) => {
    return { label: tag.text, value: tag.id };
  });
};

interface IFormValues {
  tags: IOption[];
  selectedTags: IOption[];
  selectedRoles: IOption[];
  instanceType: ISurveyItem['instanceType'];
}

class SurveySettingsViewer extends React.Component<
  IComponentProps,
  IComponentStates
> {
  formikRef = React.createRef<Formik>();
  state: IComponentStates = {
    dataError: undefined,
    savingData: false,
    savingSurveyItem: false,
    showCheckPointModal: false,
    validPhoneValues: [],
    updatingValidList: false,
    applyingTemplate: false,
  };

  componentDidMount() {
    const {
      item: {
        data: { providerId },
      },
      requireProviderTags,
      requireCheckPoints,
      survey,
      roles,
      companyId,
    } = this.props;
    requireProviderTags(providerId);
    requireCheckPoints(survey.data.id, undefined, survey.data.surveyToken);
    if (roles === undefined && companyId) this.props.getCompany(companyId);
  }

  handleSubmitData = (
    values: ISurveyView,
    withoutProviderSurveySave: boolean = false
  ) => {
    this.setState({ savingData: true });
    const { item, updateSurvey, updateProviderSurvey, withoutProviderSurvey } =
      this.props;

    return updateSurvey(values, item.data).then(
      () => {
        if (!withoutProviderSurvey && !withoutProviderSurveySave) {
          const { name } = values;
          const innerItem = {
            ...item,
            data: {
              ...item.data,
              name: name,
              surveyName: name,
            },
          };
          updateProviderSurvey(innerItem).then(
            () => {
              this.setState({ savingData: false, dataError: null });
              notify(i18n.t('surveyUpdated', { ns: 'survey' }));
            },
            (err: any) => {
              this.setState({ savingData: false, dataError: err });
            }
          );
        } else {
          this.setState({ savingData: false, dataError: null });
          notify(i18n.t('surveyUpdated', { ns: 'survey' }));
        }
      },
      (err: any) => {
        this.setState({ savingData: false, dataError: err });
      }
    );
  };

  onSubmitForm = (values: IFormValues) => {
    this.setState({ savingSurveyItem: true });
    const { item, updateProviderSurvey, survey } = this.props;
    const newItem = cloneDeep(item);
    if (values.selectedTags) {
      // We just send tag's text
      newItem.data.tags = values.selectedTags.map((tag: IOption) => {
        return {
          text: tag.label,
        } as ITagView;
      });
    }
    newItem.data.targetRolesIds = values.selectedRoles.map((role: IOption) => {
      return Number(role.value);
    });
    const oldInstanceType = item.data.instanceType;
    newItem.data.instanceType = values.instanceType;
    updateProviderSurvey(newItem).then(
      () => {
        if (
          newItem.data.instanceType !== 'User' &&
          survey.data.visibilityStrategy === 'personal'
        ) {
          const newData = cloneDeep(survey);
          newData.data.visibilityStrategy = 'open';
          newData.data.showResponseToTarget = false;
          this.handleSubmitData(newData.data);
        } else if (
          newItem.data.instanceType === 'User' &&
          oldInstanceType !== 'User' &&
          survey.data.visibilityStrategy === 'open'
        ) {
          const newData = cloneDeep(survey);
          newData.data.visibilityStrategy = 'personal';
          this.handleSubmitData(newData.data);
        } else {
          notify(i18n.t('surveyUpdated', { ns: 'survey' }));
        }
        this.setState({ savingSurveyItem: false });
      },
      (err: any) => {
        display(err);
      }
    );
  };

  convertToClassRating = () => {
    this.setState({ convertingToExam: true });
    const { item, updateProviderSurvey, survey } = this.props;
    const newItem = cloneDeep(item);

    newItem.data.instanceType = 'User';
    updateProviderSurvey(newItem).then(
      () => {
        this.formikRef.current?.setFieldValue('instanceType', 'User');
        const newData = cloneDeep(survey.data) as ISurveyView;
        newData.visibilityStrategy = 'personal';
        newData.periodType = 'none';
        newData.keepUnique = true;
        newData.uniquePerTags = true;
        newData.uniquePerInstance = true;
        newData.keepSubmitQuestionList = true;
        newData.randomizeOrder = false;
        newData.autoLock = true;
        newData.showResponseToTarget = true;
        newData.offerToFillAtFirst = true;
        newData.surveyType = 'Regular';
        const allowDraft = newData.fields.find(
          (it) => it.name === 'conf_allowDraft'
        );
        if (allowDraft) allowDraft.value = 'false';
        else newData.fields.push({ name: 'conf_allowDraft', value: 'false' });
        this.handleSubmitData(newData, true).then(() => {
          notify(i18n.t('surveyConvertedToExam', { ns: 'survey' }));
          this.setState({ convertingToExam: false });
        });
      },
      (err: any) => {
        display(err);
        this.setState({ convertingToExam: false });
      }
    );
  };

  handleShowCheckPointModal = () => {
    this.setState((prevState) => ({
      showCheckPointModal: !prevState.showCheckPointModal,
    }));
  };

  render() {
    const {
      allTags,
      item: {
        data: { tags, instanceType, targetRolesIds },
      },
      survey,
      withoutProviderSurvey,
      roles,
      companyMembers,
      supportOrOwner,
      isAdvancedAdmin,
    } = this.props;
    const viewingVersion = survey.viewingVersion!;
    const viewingOlderVersion = viewingVersion !== survey.data.currentVersion;
    const initialValues = {
      tags: allTags ? getTags(allTags, true) : [],
      selectedTags: getSelectedTags(tags),
      selectedRoles: (targetRolesIds
        ? targetRolesIds.map((r) => {
            return {
              value: r,
              label: roles && roles[r] ? roles[r].name : '-',
            };
          })
        : []) as IOption[],
      instanceType: instanceType || 'Provider',
    };

    if (survey.isLoading !== false) {
      return <p>loading...</p>;
    }
    return (
      <div
        className="modal-content flex-box"
        style={{ paddingBottom: '0', overflowX: 'hidden' }}
      >
        <h5 className="no-margin green-text text-darken-2">
          {i18n.t('answersSettings', { ns: 'survey' }) as string}
        </h5>
        <div>
          {!withoutProviderSurvey && (
            <Formik
              onSubmit={this.onSubmitForm}
              initialValues={initialValues}
              innerRef={this.formikRef}
              render={(formikBag: FormikProps<IFormValues>) => (
                //  @ts-expect-error JSX error
                <Form>
                  <Field
                    name="tags"
                    render={() => (
                      <Select
                        multiChoice={true}
                        options={formikBag.values.tags}
                        values={formikBag.values.selectedTags}
                        onChangeValues={(values) => {
                          formikBag.setFieldValue('selectedTags', values);
                        }}
                        label={i18n.t('enterTags', { ns: 'survey' }) as string}
                        placeholder={
                          i18n.t('selectTag', { ns: 'survey' }) as string
                        }
                      />
                    )}
                  />
                  <label htmlFor="instanceType">
                    {i18n.t('instanceType', { ns: 'survey' }) as string}
                  </label>
                  <Field
                    id="instanceType"
                    name="instanceType"
                    component="select"
                    className="browser-default"
                  >
                    {/*<option value="Provider">{messages.providerId}</option>*/}
                    <option value="ProviderItem">
                      {i18n.t('providerItemId', { ns: 'survey' }) as string}
                    </option>
                    <option value="User">
                      {i18n.t('user', { ns: 'survey' }) as string}
                    </option>
                  </Field>
                  {roles && formikBag.values.instanceType === 'User' && (
                    <>
                      <Field
                        name="roles"
                        render={() => (
                          <Select
                            multiChoice={true}
                            options={Object.values(roles).map((role) => {
                              return { value: role.id, label: role.name };
                            })}
                            values={formikBag.values.selectedRoles}
                            onChangeValues={(values) => {
                              formikBag.setFieldValue('selectedRoles', values);
                            }}
                            label={
                              i18n.t('appliedRoles', { ns: 'survey' }) as string
                            }
                            placeholder={
                              i18n.t('addRoles', { ns: 'survey' }) as string
                            }
                          />
                        )}
                      />
                    </>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      marginTop: '10px',
                    }}
                  >
                    <Button
                      color="green"
                      type="submit"
                      loading={this.state.savingSurveyItem}
                    >
                      {i18n.t('save', { ns: 'survey' }) as string}
                    </Button>

                    {supportOrOwner && (
                      <Button
                        loading={this.state.convertingToExam}
                        color="red-outline"
                        onClick={() => {
                          if (
                            confirm(
                              i18n.t('confirmConvertToExam', { ns: 'survey' })
                            )
                          )
                            this.convertToClassRating();
                        }}
                      >
                        {
                          i18n.t('convertToClassRating', {
                            ns: 'survey',
                          }) as string
                        }
                      </Button>
                    )}
                    {this.props.item.data.templateType ===
                      SurveyAnalyseTemplateType && (
                      <Button
                        loading={this.state.applyingTemplate}
                        color="red-outline"
                        onClick={() => {
                          if (
                            this.props.survey.data.currentVersion === 'init'
                          ) {
                            alert(
                              i18n.t('pleaseUseANewVersion', { ns: 'survey' })
                            );
                            return;
                          }
                          if (
                            confirm(
                              i18n.t('confirmApplyAnalyseTemplate', {
                                ns: 'survey',
                              })
                            )
                          ) {
                            this.setState({ applyingTemplate: true });
                            this.props
                              .applyAnalyseTemplate(
                                this.props.item.data.providerId
                              )
                              .then(() => {
                                notify(
                                  i18n.t('analyseTemplateApplied', {
                                    ns: 'survey',
                                  })
                                );
                                this.setState({ applyingTemplate: false });
                              });
                          }
                        }}
                      >
                        {
                          i18n.t('applyAnalyseTemplate', {
                            ns: 'survey',
                          }) as string
                        }
                      </Button>
                    )}
                    <APopover
                      target={
                        <Button color="black-outline">
                          <Icon type={'phone-call'} />
                          <>
                            {i18n.t('managePhoneDirectAcceess', {
                              ns: 'survey',
                            })}
                            :
                            <span
                              style={{
                                color: isANumber(
                                  this.props.survey.data.validListSize
                                )
                                  ? green
                                  : red,
                              }}
                            >
                              {isANumber(this.props.survey.data.validListSize)
                                ? (i18n.t('active', { ns: 'survey' }) as string)
                                : (i18n.t('inactive', {
                                    ns: 'survey',
                                  }) as string)}
                            </span>
                          </>
                        </Button>
                      }
                      curveBorder
                      arrow
                      placement={'bottom-end'}
                    >
                      {
                        <div>
                          <QuestionHintBox>
                            <HintIcon type="info" />
                            <HintText>
                              <div>
                                {
                                  i18n.t('validListInfo', {
                                    ns: 'survey',
                                  }) as string
                                }
                              </div>
                            </HintText>
                          </QuestionHintBox>
                          <p>
                            {
                              i18n.t('validListCount', {
                                ns: 'survey',
                              }) as string
                            }
                            {this.props.survey.data.validListSize || 0}
                          </p>
                          <Select
                            label={
                              i18n.t('addToPhoneValidList', {
                                ns: 'survey',
                              }) as string
                            }
                            onChangeValues={(values) => {
                              this.setState({
                                validPhoneValues: values.map((it) =>
                                  it.value.toString()
                                ),
                              });
                            }}
                            placeholder={
                              i18n.t('enterPhoneNumber', {
                                ns: 'survey',
                              }) as string
                            }
                            values={this.state.validPhoneValues.map((it) => ({
                              label: it,
                              value: it,
                            }))}
                            multiChoice={true}
                            options={[]}
                            letAddChip={true}
                          />
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Button
                              color={'green'}
                              loading={this.state.updatingValidList}
                              onClick={() => {
                                this.setState({ updatingValidList: true });
                                this.props
                                  .addToPhoneValidList(
                                    this.state.validPhoneValues,
                                    this.props.item.data
                                  )
                                  .then(() => {
                                    notify(
                                      i18n.t('validListUpdated', {
                                        ns: 'survey',
                                      })
                                    );
                                  })
                                  .catch((e: any) => {
                                    notifyError(e);
                                  })
                                  .finally(() => {
                                    this.setState({
                                      updatingValidList: false,
                                    });
                                  });
                              }}
                            >
                              {
                                i18n.t('addToPhoneValidList', {
                                  ns: 'survey',
                                }) as string
                              }
                            </Button>
                            <Button
                              loading={this.state.updatingValidList}
                              onClick={() => {
                                if (
                                  confirm(
                                    i18n.t('removeValidListConfirm', {
                                      ns: 'survey',
                                    })
                                  )
                                ) {
                                  this.setState({ updatingValidList: true });
                                  this.props
                                    .removeValidList(this.props.item.data)
                                    .then(() => {
                                      notify(
                                        i18n.t('validListRemoved', {
                                          ns: 'survey',
                                        })
                                      );
                                    })
                                    .catch((e: any) => {
                                      notifyError(e);
                                    })
                                    .finally(() => {
                                      this.setState({
                                        updatingValidList: false,
                                      });
                                    });
                                }
                              }}
                            >
                              {
                                i18n.t('removeValidList', {
                                  ns: 'survey',
                                }) as string
                              }
                            </Button>
                          </div>
                        </div>
                      }
                    </APopover>
                  </div>
                </Form>
              )}
            />
          )}
          <div className="section">
            <SurveySettingsForm
              saving={this.state.savingData}
              onSubmit={(data) => this.handleSubmitData(data)}
              tags={this.props.tags}
              readOnly={viewingOlderVersion}
              supportOrOwner={supportOrOwner}
              isAdvancedAdmin={isAdvancedAdmin}
              initialName={survey.data.versions[viewingVersion!].name}
              initialFields={survey.data.versions[viewingVersion!].fields}
              initialAutoLock={survey.data.versions[viewingVersion!].autoLock}
              initialAskTitle={survey.data.versions[viewingVersion].askTitle}
              initialOfferToFillAtFirst={
                survey.data.versions[viewingVersion].offerToFillAtFirst
              }
              initialSensitiveChangeRequiresEditor={
                survey.data.versions[viewingVersion]
                  .sensitiveChangeRequiresEditor
              }
              initialKeepUnique={
                survey.data.versions[viewingVersion].keepUnique
              }
              initialPeriodType={
                survey.data.versions[viewingVersion].periodType
              }
              initialDescription={
                survey.data.versions[viewingVersion].description
              }
              initialUniquePerTime={
                survey.data.versions[viewingVersion].uniquePerTime
              }
              initialVersionTitle={
                survey.data.versions[viewingVersion].versionInfo!.versionTitle!
              }
              initialVersionDescription={
                survey.data.versions[viewingVersion].versionInfo!
                  .versionDescription!
              }
              initialUniquePerUser={
                survey.data.versions[viewingVersion].uniquePerUser
              }
              initialKeepResponsesAnonymous={survey.data.keepResponsesAnonymous}
              initialRecommendRefill={
                survey.data.versions[viewingVersion].recommendRefill
              }
              initialUniquePerInstance={
                survey.data.versions[viewingVersion].uniquePerInstance
              }
              initialRandomizeOrder={
                survey.data.versions[viewingVersion].randomizeOrder
              }
              initialKeepSubmitQuestionList={
                survey.data.versions[viewingVersion].keepSubmitQuestionList
              }
              initialUniquePerTags={
                survey.data.versions[viewingVersion].uniquePerTags
              }
              initialScoreMethod={
                survey.data.versions[viewingVersion].scoreMethod!
              }
              initialTrackResponseChanges={
                survey.data.versions[viewingVersion].trackResponseChanges!
              }
              initialVisibilityStrategy={survey.data.visibilityStrategy!}
              initialShowResponseToTarget={survey.data.showResponseToTarget}
              initialKeepLatestInstanceIfAnonymous={
                survey.data.versions[viewingVersion]
                  .keepLatestInstanceIfAnonymous
              }
              initialStageQuestion={
                survey.data.versions[viewingVersion].stageQuestion
              }
              initialFillValidPeriod={survey.data.fillValidPeriod}
              instanceType={instanceType}
              survey={survey.data}
              companyMembers={companyMembers}
              requireSeveralUser={this.props.requireSeveralUser}
              onlyEditNotifGroup={this.props.onlyEditNotifGroups}
              token={this.props.token}
              companyRoles={roles}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const providerId = props.item.data.providerId;
  const provider = state.providers.map[providerId];
  const companyId = provider.data!.managingCompanyId!;
  const companyRoles = state.companies.data[companyId]?.roles;
  const companyMembers = state.companies.data[companyId]?.members;
  const tags = state.providers.tags[providerId]!.data;
  const token = getAccessToken(state)!;

  const thisUser = state.thisUser;
  const company = state.companies.data[companyId];
  const isSupport = company && isCompanySupport(thisUser, company);
  const isSelfProvider =
    provider && provider.data?.user === Number(thisUser.userId);

  const isAdvancedAdmin = isAdvanceAdmin(thisUser, company);

  return {
    roles: companyRoles,
    companyMembers: companyMembers,
    companyId,
    tags,
    token,
    supportOrOwner: isSupport || isSelfProvider,
    isAdvancedAdmin,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    updateSurvey: (
      surveyData: ISurveyView,
      providerSurveyItemData: ISurveyItem
    ) => appDispatch(updateSurvey({ surveyData, providerSurveyItemData })),
    addToPhoneValidList: (
      validList: string[],
      providerSurveyItemData: ISurveyItem
    ) =>
      appDispatch(
        addToPhoneValidList({
          phoneValidList: validList,
          providerSurveyItemData,
        })
      ),
    applyAnalyseTemplate: (providerId: number) =>
      appDispatch(
        applyAnalyseTemplate({
          providerId,
        })
      ),
    removeValidList: (providerSurveyItemData: ISurveyItem) =>
      appDispatch(removePhoneValidList({ providerSurveyItemData })),
    getCompany: (companyId: number) =>
      appDispatch(
        getCompanyHandler({
          companyId,
          templateStub: true,
          companyStub: false,
          projectStub: true,
          taskStub: true,
        })
      ),
    requireProviderTags: (providerId: number) =>
      appDispatch(requireProviderTags({ providerId })),
    updateProviderSurvey: (surveyItem: ISurveyItemWithData) =>
      appDispatch(providerUpdateSurvey({ item: surveyItem.data })),
    requireCheckPoints: (
      surveyId?: number,
      reportId?: number,
      surveyToken?: string,
      ownerToken?: string
    ) =>
      appDispatch(
        requireCheckPoints({
          surveyId,
          reportId,
          surveyToken,
          ownerToken,
        })
      ),
    requireSeveralUser: (usersList: number[]) =>
      appDispatch(requireSeveralUser({ usersList })),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SurveySettingsViewer);
